import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { Plus, Edit01, Trash01, SearchLg, Eye } from '@untitledui/icons';
import { Button } from '@/components/base/buttons/button';
import { Input } from '@/components/base/input/input';
import { Select } from '@/components/base/select/select';
import { Badge } from '@/components/base/badges/badges';
import { Table, TableCard } from '@/components/application/table/table';
import { Modal, DialogTrigger, Dialog, ModalOverlay } from '@/components/application/modals/modal';
import { useTranslation } from 'react-i18next';
import { useKnowledgeBases, useDeleteKnowledgeBase } from '@/hooks/api/use-knowledge-bases';
import type { KnowledgeBaseListFilters } from '@/types/api/knowledge-bases';
import type { KnowledgeBaseType } from '@/types/knowledge-base';
import { useToast } from '@/hooks/use-toast';

export const KnowledgeBaseListPage = () => {
    const { t } = useTranslation(['pages:knowledge-admin', 'common']);
    const navigate = useNavigate();
    const toast = useToast();
    const [searchTerm, setSearchTerm] = useState('');
    const [typeFilter, setTypeFilter] = useState<KnowledgeBaseType | ''>('');
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [kbToDelete, setKbToDelete] = useState<string | null>(null);

    // API hooks
    const filters: KnowledgeBaseListFilters = useMemo(() => ({
        search: searchTerm || undefined,
        kb_type: typeFilter || undefined,
        limit: 500,
    }), [searchTerm, typeFilter]);

    const { data: allKnowledgeBases = [], isLoading, isError } = useKnowledgeBases(filters);
    const deleteKnowledgeBase = useDeleteKnowledgeBase();

    // Client-side filtering if needed (already handled by API)
    const knowledgeBases = allKnowledgeBases;

    const typeOptions = [
        { id: '', label: t('typePlaceholder') },
        { id: 'general', label: t('types.general') },
        { id: 'documents', label: t('types.documents') },
        { id: 'routes', label: t('types.routes') },
        { id: 'regulation', label: t('types.regulation') },
        { id: 'company/account-specific', label: t('types.companyAccountSpecific') },
    ];

    const handleDeleteClick = (id: string) => {
        setKbToDelete(id);
        setIsDeleteModalOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!kbToDelete) return;

        try {
            await deleteKnowledgeBase.mutateAsync(kbToDelete);
            toast.success(t('messages.deleteSuccess', { defaultValue: 'Knowledge base eliminata con successo' }));
            setIsDeleteModalOpen(false);
            setKbToDelete(null);
        } catch (error) {
            console.error('Error deleting knowledge base:', error);
            toast.error(t('messages.deleteError', { defaultValue: 'Errore nell\'eliminazione della knowledge base' }));
        }
    };

    const handleDeleteCancel = () => {
        setIsDeleteModalOpen(false);
        setKbToDelete(null);
    };

    const getTypeBadge = (type: KnowledgeBaseType) => {
        const typeLabels: Record<KnowledgeBaseType, string> = {
            general: t('types.general'),
            documents: t('types.documents'),
            routes: t('types.routes'),
            regulation: t('types.regulation'),
            'company/account-specific': t('types.companyAccountSpecific'),
        };
        return <Badge color="gray" size="sm">{typeLabels[type]}</Badge>;
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand mx-auto mb-4"></div>
                    <p className="text-tertiary">{t('loading')}</p>
                </div>
            </div>
        );
    }

    if (isError) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <p className="text-error mb-4">{t('messages.loadError', { defaultValue: 'Errore nel caricamento delle knowledge bases' })}</p>
                    <Button onClick={() => window.location.reload()} size="md" color="primary">
                        {t('common.retry', { defaultValue: 'Riprova' })}
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-semibold text-primary">{t('title')}</h2>
                    <p className="text-tertiary mt-1">{t('subtitle')}</p>
                </div>
                <Button
                    onClick={() => navigate('/admin/knowledge/new')}
                    size="md"
                    color="primary"
                    iconLeading={Plus}
                >
                    {t('newKnowledgeBase')}
                </Button>
            </div>

            {/* Filters */}
            <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <Input
                        placeholder={t('searchPlaceholder')}
                        value={searchTerm}
                        onChange={setSearchTerm}
                        icon={SearchLg}
                        size="md"
                    />

                    <Select
                        placeholder={t('typePlaceholder')}
                        items={typeOptions}
                        selectedKey={typeFilter}
                        onSelectionChange={(key) => setTypeFilter(key as KnowledgeBaseType | '')}
                        size="md"
                    >
                        {(item) => (
                            <Select.Item key={item.id} id={item.id}>
                                {item.label}
                            </Select.Item>
                        )}
                    </Select>
                </div>
            </div>

            {/* Knowledge Bases Table */}
            <TableCard.Root>
                <TableCard.Header
                    title={`${t('title')} (${knowledgeBases.length})`}
                    description={t('subtitle')}
                />

                <Table>
                    <Table.Header>
                        <Table.Head label={t('headers.name')} isRowHeader />
                        <Table.Head label={t('headers.description')} />
                        <Table.Head label={t('headers.type')} />
                        <Table.Head label={t('headers.documentCount')} />
                        <Table.Head label={t('headers.createdAt')} />
                        <Table.Head label={t('headers.actions')} />
                    </Table.Header>
                    <Table.Body>
                        {knowledgeBases.map((kb) => (
                            <Table.Row key={kb.knowledge_base_id}>
                                <Table.Cell>
                                    <div className="font-medium text-primary">{kb.name}</div>
                                </Table.Cell>
                                <Table.Cell>
                                    <span className="text-sm text-tertiary">
                                        {kb.description || '-'}
                                    </span>
                                </Table.Cell>
                                <Table.Cell>
                                    {getTypeBadge(kb.kb_type)}
                                </Table.Cell>
                                <Table.Cell>
                                    <span className="text-sm text-primary">{kb.document_count}</span>
                                </Table.Cell>
                                <Table.Cell>
                                    <span className="text-sm text-tertiary">
                                        {new Date(kb.created_at).toLocaleDateString()}
                                    </span>
                                </Table.Cell>
                                <Table.Cell>
                                    <div className="flex items-center gap-2">
                                        <Button
                                            onClick={() => navigate(`/admin/knowledge/${kb.knowledge_base_id}`)}
                                            size="sm"
                                            color="secondary"
                                            iconLeading={Eye}
                                        >
                                            {t('actions.view')}
                                        </Button>
                                        <Button
                                            onClick={() => navigate(`/admin/knowledge/${kb.knowledge_base_id}`)}
                                            size="sm"
                                            color="secondary"
                                            iconLeading={Edit01}
                                        >
                                            {t('actions.edit')}
                                        </Button>
                                        <Button
                                            onClick={() => handleDeleteClick(kb.knowledge_base_id)}
                                            size="sm"
                                            color="secondary-destructive"
                                            iconLeading={Trash01}
                                        >
                                            {t('actions.delete')}
                                        </Button>
                                    </div>
                                </Table.Cell>
                            </Table.Row>
                        ))}
                    </Table.Body>
                </Table>
            </TableCard.Root>

            {knowledgeBases.length === 0 && (
                <div className="text-center py-12">
                    <div className="text-tertiary mb-4">
                        <h3 className="text-lg font-medium text-primary mb-2">{t('noData.title')}</h3>
                        <p className="text-tertiary">{t('noData.message')}</p>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            <DialogTrigger isOpen={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
                <ModalOverlay>
                    <Modal>
                        <Dialog>
                            <div className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary">
                                <h2 className="text-xl font-semibold text-primary mb-4">
                                    {t('modal.confirmDelete')}
                                </h2>
                                <p className="text-tertiary mb-6">
                                    {t('modal.confirmDeleteMessage')}
                                </p>
                                <div className="flex items-center justify-end gap-4">
                                    <Button
                                        onClick={handleDeleteCancel}
                                        size="md"
                                        color="secondary"
                                        isDisabled={deleteKnowledgeBase.isPending}
                                    >
                                        {t('modal.cancel')}
                                    </Button>
                                    <Button
                                        onClick={handleDeleteConfirm}
                                        size="md"
                                        color="primary-destructive"
                                        isDisabled={deleteKnowledgeBase.isPending}
                                    >
                                        {deleteKnowledgeBase.isPending ? t('loading') : t('modal.delete')}
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            </DialogTrigger>
        </div>
    );
};
